import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  inject,
  signal,
} from '@angular/core';
import { LazyLoadContainerComponent } from '../../lazy-load-container/lazy-load-container.component';
import { ReactiveFormsModule } from '@angular/forms';
import { SidebarComponent } from '../../../sidebar.component';
import { SidebarApiService } from '../../../services/sidebar-api.service';
import { GetTaskGroupsParams } from 'src/app/shared/models/tasks.model';
import {
  SidebarListsContract,
  SidebarTaskGroupUi,
  SidebarTaskUi,
} from '../../../model/sidebar.model';
import {
  LimitedResultOfTaskGroupDto,
  TaskGroupDto,
} from '@r1-development/resolution-mechanicalturk-api-client';
import { SidebarFlatTasksListComponent } from '../../sidebar-flat-tasks-list/sidebar-flat-tasks-list.component';
import { setSortByDirection } from 'src/app/shared/utils/global.utils';

@Component({
  selector: 'ri-resolution-ui-search-result-tasks',
  imports: [
    SidebarFlatTasksListComponent,
    LazyLoadContainerComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './search-result-tasks.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchResultTasksComponent implements SidebarListsContract<GetTaskGroupsParams> {
  private readonly sidebarApiService = inject(SidebarApiService);

  private readonly sidebarComponentComponent = inject<SidebarComponent>(
    forwardRef(() => SidebarComponent)
  );

  public readonly allowedTaskTypes =
    this.sidebarComponentComponent.allowedTaskTypes;

  private taskGroupsListGenerator?: AsyncGenerator<LimitedResultOfTaskGroupDto>;

  private readonly _groups = signal<SidebarTaskGroupUi[]>([]);
  public readonly groups = this._groups.asReadonly();

  private readonly _hasGroupsToFetch = signal<boolean>(false);
  public readonly hasGroupsToFetch = this._hasGroupsToFetch.asReadonly();

  private readonly _isLoading = signal<boolean>(false);
  public readonly isLoading = this._isLoading.asReadonly();

  public readonly flatTasksList = computed<SidebarTaskUi[]>(() => {
    const groups = this.groups();

    if (groups.length === 0) {
      return [];
    }

    return groups.flatMap((group) => group.tasks);
  });

  public readonly hasTasksLessThanLimit = computed<boolean>(() => {
    const groups = this.groups();
    return (
      groups.length > 0 && groups.length < SidebarApiService.LOAD_PAGE_SIZE
    );
  });

  public readonly hasNoTasks = computed<boolean>(() => {
    const groups = this.groups();
    return groups.length === 0;
  });

  reloadData(params: GetTaskGroupsParams): void {
    this._groups.set([]);
    this._hasGroupsToFetch.set(false);

    this.taskGroupsListGenerator =
      this.sidebarApiService.createAllTasksListGenerator({
        ...params,
        sort: [
          setSortByDirection<TaskGroupDto>('status', 'desc'),
          setSortByDirection<TaskGroupDto>('createdAt', 'desc'),
        ],
      });

    this.loadTaskGroups({ reload: true });
  }

  async loadTaskGroups(
    options: { reload: boolean } = { reload: false }
  ): Promise<void> {
    this._isLoading.set(true);

    try {
      const result = await SidebarComponent.loadTaskGroups(
        this.taskGroupsListGenerator!
      );

      if (result.done) {
        return;
      }

      if (options.reload) {
        this._groups.set(result.groups);
      } else {
        this._groups.update((oldGroups) => [...oldGroups, ...result.groups]);
      }

      this._hasGroupsToFetch.set(result.hasMore);
    } finally {
      this._isLoading.set(false);
    }
  }
}
